/**
 * Debug state exports: split into 3 focused categories for faster diagnosis.
 *
 * 1. Physics Snapshot — client vs server physics comparison (lightweight, instant)
 * 2. Timing & Performance — frame timing buffer + browser info
 * 3. Reconciliation Log — ring buffer of recent reconciliation events + asteroid sync
 *
 * Also provides scenery-specific export for diagnosing cross-client visual
 * differences (seed derivation, PRNG sequences, nebula params, etc).
 */

import { debugExportClientState, debugExportPredictionState, getAsteroidPositions } from './ClientPrediction';
import { computeFrameTimingStats, getBufferSnapshot, getBrowserInfo } from './FrameTimingBuffer';
import { getReconciliationLog, getReconciliationStats } from './ReconciliationLog';
import { getAsteroidSyncLog, getAsteroidSyncStats } from './AsteroidSyncLog';
import { useGameStore } from '../../stores/gameStore';
import { mulberry32, TICK_DELTA } from '@warmancer/shared';

const API_BASE_URL = import.meta.env.DEV
  ? 'http://localhost:8788'
  : 'https://api.warmancer.com';

/**
 * Fetch server debug state from the API.
 * Returns null if the fetch fails (server not running, etc).
 */
async function fetchServerState(): Promise<unknown> {
  try {
    const res = await fetch(`${API_BASE_URL}/game/debug`);
    if (!res.ok) return { error: `HTTP ${res.status}: ${res.statusText}` };
    return await res.json();
  } catch (err) {
    return { error: err instanceof Error ? err.message : String(err) };
  }
}

/**
 * Compute per-entity diffs between client and server asteroid states.
 */
function computeAsteroidDiffs(
  clientAsteroids: ReadonlyArray<{ x: number; y: number; vx: number; vy: number; radius: number }>,
  serverAsteroids: ReadonlyArray<{ x: number; y: number; vx: number; vy: number }>,
): Array<{ index: number; dx: number; dy: number; dvx: number; dvy: number; distance: number }> {
  const diffs: Array<{ index: number; dx: number; dy: number; dvx: number; dvy: number; distance: number }> = [];
  const count = Math.min(clientAsteroids.length, serverAsteroids.length);
  for (let i = 0; i < count; i++) {
    const dx = clientAsteroids[i].x - serverAsteroids[i].x;
    const dy = clientAsteroids[i].y - serverAsteroids[i].y;
    const dvx = clientAsteroids[i].vx - serverAsteroids[i].vx;
    const dvy = clientAsteroids[i].vy - serverAsteroids[i].vy;
    diffs.push({ index: i, dx, dy, dvx, dvy, distance: Math.sqrt(dx * dx + dy * dy) });
  }
  return diffs;
}

// ---------------------------------------------------------------------------
// Export 1: Physics Snapshot
// ---------------------------------------------------------------------------

/**
 * Export client vs server physics state comparison.
 * The key diagnostic for "why do things look wrong right now?"
 * Includes per-entity diffs for ships and asteroids.
 */
export async function exportPhysicsSnapshot(ts?: number): Promise<void> {
  const timestamp = ts ?? Date.now();
  const store = useGameStore.getState();
  const clientPhysics = debugExportClientState();
  const predictionState = debugExportPredictionState();
  const clientAsteroids = getAsteroidPositions();

  const serverState = await fetchServerState() as Record<string, unknown> | null;

  // Extract server asteroid data for diffing (if available)
  let asteroidDiffs: ReturnType<typeof computeAsteroidDiffs> = [];
  if (serverState && typeof serverState === 'object' && 'physics' in serverState) {
    const physics = serverState.physics as Record<string, unknown> | undefined;
    if (physics && 'asteroids' in physics && Array.isArray(physics.asteroids)) {
      asteroidDiffs = computeAsteroidDiffs(clientAsteroids, physics.asteroids as Array<{ x: number; y: number; vx: number; vy: number }>);
    }
  }

  // Compute ship diff
  let shipDiff: { dx: number; dy: number; dvx: number; dvy: number; distance: number } | null = null;
  if (store.localShip && store.serverShip) {
    const dx = store.localShip.x - store.serverShip.x;
    const dy = store.localShip.y - store.serverShip.y;
    const dvx = store.localShip.vx - store.serverShip.vx;
    const dvy = store.localShip.vy - store.serverShip.vy;
    shipDiff = { dx, dy, dvx, dvy, distance: Math.sqrt(dx * dx + dy * dy) };
  }

  // Serialize remote players map
  const remotePlayersObj: Record<number, unknown> = {};
  for (const [entityId, remote] of store.remotePlayers) {
    remotePlayersObj[entityId] = {
      username: remote.username,
      state: remote.state,
    };
  }
  const remotePredictedObj: Record<number, unknown> = {};
  for (const [entityId, pos] of store.remotePredictedPositions) {
    remotePredictedObj[entityId] = pos;
  }

  const payload = {
    exportType: 'physics-snapshot',
    timestamp: new Date(timestamp).toISOString(),
    store: {
      serverTick: store.serverTick,
      clientTick: store.clientTick,
      lastSnapshotTick: store.lastSnapshotTick,
      moonAngle: store.moonAngle,
      localEntityId: store.localEntityId,
      localShip: store.localShip,
      serverShip: store.serverShip,
      desyncDistance: store.desyncDistance,
      pendingInputsCount: store.pendingInputs.length,
      pendingInputs: store.pendingInputs,
      connectionStatus: store.connectionStatus,
      remotePlayers: remotePlayersObj,
      remotePredictedPositions: remotePredictedObj,
    },
    prediction: predictionState,
    clientPhysics,
    clientAsteroids,
    diffs: {
      ship: shipDiff,
      asteroids: asteroidDiffs,
    },
    server: serverState,
  };

  const json = JSON.stringify(payload, null, 2);
  downloadJson(json, `warmancer-physics-${timestamp}.json`);
}

// ---------------------------------------------------------------------------
// Export 2: Timing & Performance
// ---------------------------------------------------------------------------

/**
 * Export frame timing statistics and recent frame buffer.
 * Useful for diagnosing framerate/accumulator bugs, not simulation divergence.
 */
export function exportTimingState(ts?: number): void {
  const timestamp = ts ?? Date.now();

  const payload = {
    exportType: 'timing-performance',
    timestamp: new Date(timestamp).toISOString(),
    stats: computeFrameTimingStats(),
    recentFrames: getBufferSnapshot(),
    browserInfo: getBrowserInfo(),
  };

  const json = JSON.stringify(payload, null, 2);
  downloadJson(json, `warmancer-timing-${timestamp}.json`);
}

// ---------------------------------------------------------------------------
// Export 3: Reconciliation Log
// ---------------------------------------------------------------------------

/**
 * Export reconciliation event history and asteroid sync corrections.
 * The data needed to answer "why is my ship rubber-banding?"
 */
export function exportReconciliationLog(ts?: number): void {
  const timestamp = ts ?? Date.now();

  const payload = {
    exportType: 'reconciliation-log',
    timestamp: new Date(timestamp).toISOString(),
    reconciliation: {
      stats: getReconciliationStats(),
      events: getReconciliationLog(),
    },
    asteroidSync: {
      stats: getAsteroidSyncStats(),
      events: getAsteroidSyncLog(),
    },
  };

  const json = JSON.stringify(payload, null, 2);
  downloadJson(json, `warmancer-reconciliation-${timestamp}.json`);
}

// ---------------------------------------------------------------------------
// Export All (convenience)
// ---------------------------------------------------------------------------

/**
 * Capture all 3 exports simultaneously with matching timestamps.
 * Downloads 3 separate files for focused analysis.
 */
export async function exportAll(): Promise<void> {
  const ts = Date.now();
  // Physics is async (server fetch), timing and reconciliation are sync.
  // Run them in parallel.
  await Promise.all([
    exportPhysicsSnapshot(ts),
    Promise.resolve(exportTimingState(ts)),
    Promise.resolve(exportReconciliationLog(ts)),
  ]);
}

// ---------------------------------------------------------------------------
// Scenery Export (unchanged)
// ---------------------------------------------------------------------------

/**
 * Derive background seeds identically to SpaceBackground.tsx deriveSeeds().
 * Duplicated here to capture the exact values without importing React components.
 */
function deriveBackgroundSeeds(planet: { mass: number; radius: number }, moon: { orbitSpeed: number }, masterSeed?: number) {
  const base = masterSeed !== undefined
    ? masterSeed
    : Math.floor(Math.abs(planet.mass * 137 + planet.radius * 311 + moon.orbitSpeed * 719));
  return {
    usedMasterSeed: masterSeed !== undefined,
    base,
    dust: base ^ 0xDEAD0001,
    stars: base ^ 0xBEEF0002,
    nearStars: base ^ 0xCAFE0003,
    debris: base ^ 0xFACE0004,
  };
}

/**
 * Sample first N values from a mulberry32 PRNG for a given seed.
 * Proves determinism or exposes divergence across clients.
 */
function samplePrng(seed: number, count: number): number[] {
  const rng = mulberry32(seed);
  const values: number[] = [];
  for (let i = 0; i < count; i++) {
    values.push(rng());
  }
  return values;
}

function fract(x: number): number {
  return x - Math.floor(x);
}

/**
 * Export scenery-specific debug state for diagnosing cross-client visual differences.
 * Captures seed derivation, PRNG sequences, nebula params, browser info, and server scenery.
 */
export async function exportSceneryState(): Promise<void> {
  const store = useGameStore.getState();
  const { scenerySetup, worldSetup } = store;

  // World setup summary
  const worldSummary = worldSetup ? {
    planetMass: worldSetup.planet.mass,
    planetRadius: worldSetup.planet.radius,
    moonOrbitSpeed: worldSetup.moon.orbitSpeed,
    moonRadius: worldSetup.moon.radius,
    moonMass: worldSetup.moon.mass,
    moonOrbitRadius: worldSetup.moon.orbitRadius,
    boundsHalfSize: worldSetup.boundsHalfSize,
    asteroidCount: worldSetup.asteroids.length,
  } : null;

  // Derived background seeds
  const masterSeed = scenerySetup?.masterSeed;
  const backgroundSeeds = worldSetup
    ? deriveBackgroundSeeds(worldSetup.planet, worldSetup.moon, masterSeed)
    : null;

  // PRNG samples per background layer (first 10 values each)
  const prngSamples = backgroundSeeds ? {
    dust: samplePrng(backgroundSeeds.dust, 10),
    stars: samplePrng(backgroundSeeds.stars, 10),
    nearStars: samplePrng(backgroundSeeds.nearStars, 10),
    debris: samplePrng(backgroundSeeds.debris, 10),
  } : null;

  // Per-asteroid seeds and first PRNG sample
  const asteroidSeeds = worldSetup ? worldSetup.asteroids.map((_, i) => {
    const seed = ((masterSeed ?? 0) ^ 0xA57E0000) + i;
    return { index: i, seed, firstPrngValues: samplePrng(seed, 10) };
  }) : null;

  // VolumetricNebula seed vector (matches VolumetricNebula.tsx derivation)
  const nebulaSeed = worldSetup ? {
    x: fract(worldSetup.planet.mass * 0.1),
    y: fract(worldSetup.planet.radius * 0.3),
    z: fract(worldSetup.moon.orbitSpeed * 7.0),
  } : null;

  // Timing info for diagnosing cross-client time divergence
  const timingInfo = {
    serverTick: store.serverTick,
    serverTimeBase: store.serverTick * TICK_DELTA,
    clientTick: store.clientTick,
  };

  // Browser info
  const browserInfo = {
    userAgent: navigator.userAgent,
    windowWidth: window.innerWidth,
    windowHeight: window.innerHeight,
    devicePixelRatio: window.devicePixelRatio,
  };

  // Server scenery state
  const serverState = await fetchServerState();

  const sceneryPayload = {
    timestamp: new Date().toISOString(),
    scenerySetup,
    worldSummary,
    backgroundSeeds,
    prngSamples,
    asteroidSeeds,
    nebulaSeed,
    timingInfo,
    browserInfo,
    server: serverState,
  };

  const json = JSON.stringify(sceneryPayload, null, 2);
  downloadJson(json, `warmancer-scenery-${Date.now()}.json`);
}

/**
 * Download a JSON string as a file.
 */
function downloadJson(json: string, filename: string): void {
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
